import React, { useState, useEffect, useImperativeHandle, forwardRef } from 'react';
import {
    Table,
    Spin,
    Tag,
} from 'antd';
import { formatDateTime, getPredictionColor } from '@/shared/utils/helpers';
import { colors } from '@/shared/tokens/colors';

/**
 * RawDataTable component for displaying raw request data in the Production Monitoring Dashboard
 * 
 * @param {Object} props
 * @param {Array} props.data - The raw request data to display
 * @param {boolean} props.loading - Whether data is currently loading
 * @param {Object} props.predictionDistribution - Distribution data for predictions
 * @param {number} props.totalRecords - Total number of records available
 * @param {Function} props.onPaginationChange - Callback when pagination changes
 * @param {Function} props.onTableChange - Callback when table changes (pagination, filters, sorter)
 * @param {Function} props.onClearAllFilters - Callback when all filters are cleared (including time range)
 * @param {Object} props.activeFilters - Current active filters applied from the top filter bar
 */
const RawDataTable = forwardRef(({
    data,
    loading,
    totalRecords = 0,
    onPaginationChange,
    onTableChange,
    onClearAllFilters,
    allLabels,
}, ref) => {
    // Local state for current pagination settings
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
    });

    // Add local sorting state
    const [localSorting, setLocalSorting] = useState(null);

    // Use ref to track if only sorting changed vs pagination/filters
    const prevPaginationRef = React.useRef({ current: 1, pageSize: 10 });

    // Function to perform client-side sorting
    const getSortedData = () => {
        if (!data || !localSorting || !localSorting.field) {
            return data;
        }

        return [...data].sort((a, b) => {
            const aValue = a[localSorting.field];
            const bValue = b[localSorting.field];
            const direction = localSorting.order === 'ascend' ? 1 : -1;

            // Handle different field types appropriately
            if (localSorting.field === 'timestamp') {
                // For dates, use Date comparison
                const aDate = new Date(aValue || 0);
                const bDate = new Date(bValue || 0);
                return (aDate - bDate) * direction;
            }

            if (localSorting.field === 'responseTime') {
                // For numbers, use numeric comparison
                const aNum = parseFloat(aValue) || 0;
                const bNum = parseFloat(bValue) || 0;
                return (aNum - bNum) * direction;
            }

            // For strings, use locale comparison
            if (typeof aValue === 'string' && typeof bValue === 'string') {
                return aValue.localeCompare(bValue) * direction;
            }

            // Handle null/undefined values
            if (aValue === undefined || aValue === null) return direction;
            if (bValue === undefined || bValue === null) return -direction;

            // Default comparison for other types
            return ((aValue > bValue) ? 1 : -1) * direction;
        });
    };

    // Handle local sorting change
    const handleLocalSortingChange = (sorter) => {
        if (sorter && sorter.field && sorter.order) {
            setLocalSorting(sorter);
        } else {
            setLocalSorting(null);
        }
    };

    // Expose clearFilters method to parent components
    useImperativeHandle(ref, () => ({
        clearFilters: handleClearFilters
    }));

    // Clear all filters
    const handleClearFilters = () => {
        // Reset pagination to page 1
        setPagination(prev => ({
            ...prev,
            current: 1
        }));

        // Clear local sorting
        setLocalSorting(null);

        // Notify parent component to refresh data without filters
        if (onPaginationChange) {
            onPaginationChange(1, pagination.pageSize);
        }

        // Notify parent component to clear other filters (like time range)
        if (onClearAllFilters) {
            onClearAllFilters();
        }
    };

    // Handle table changes (pagination, filtering, sorting)
    const handleTableChange = (pagination, filters, sorter) => {
        // Always update local sorting state for immediate client-side sorting
        handleLocalSortingChange(sorter);

        // Check if only sorting changed (not pagination)
        const onlySortingChanged =
            pagination.current === prevPaginationRef.current.current &&
            pagination.pageSize === prevPaginationRef.current.pageSize &&
            Object.keys(filters || {}).length === 0;

        // Update ref with current pagination values
        prevPaginationRef.current = {
            current: pagination.current,
            pageSize: pagination.pageSize
        };

        // Update local pagination state
        setPagination(pagination);

        // For pure sorting changes, use client-side sorting only (faster UX)
        if (sorter && sorter.column && onlySortingChanged) {
            return;
        }

        // For pagination or filter changes, call the parent
        if (onTableChange) {
            onTableChange(pagination, filters, sorter);
        } else if (onPaginationChange) {
            // Backward compatibility
            onPaginationChange(pagination.current, pagination.pageSize);
        }
    };

    // Get the data to display - use sorted data if available
    const displayData = localSorting ? getSortedData() : data;

    const columns = [
        {
            title: 'Request ID',
            dataIndex: 'original_id',
            key: 'original_id',
            render: (text) => <a>{text}</a>,
        },
        {
            title: 'Created At',
            dataIndex: 'timestamp',
            key: 'timestamp',
            // Use client-side sorting with an explicit sorter function
            sorter: (a, b) => {
                const aDate = new Date(a.timestamp);
                const bDate = new Date(b.timestamp);
                return aDate - bDate;
            },
            // Keep sortField for server-side sorting fallback
            sortField: 'timestamp',
            render: (text) => formatDateTime(text),
        },
        {
            title: 'Project',
            dataIndex: 'application',
            key: 'application',
        },
        {
            title: 'Task',
            dataIndex: 'usecase',
            key: 'usecase',
        },
        {
            title: 'Response Time',
            dataIndex: 'responseTime',
            key: 'responseTime',
            // Use client-side sorting with an explicit sorter function
            sorter: (a, b) => a.responseTime - b.responseTime,
            // Keep sortField for server-side sorting fallback
            sortField: 'responseTime',
            render: (text) => `${text.toFixed(2)} ms`,
        },
        {
            title: 'Model',
            dataIndex: 'isFromOptuneModel',
            key: 'isFromOptuneModel',
            render: (isFromOptuneModel, record) => (
                <Tag color={isFromOptuneModel ? 'blue' : 'default'}>
                    {isFromOptuneModel ? 'Optune' : record.modelname}
                </Tag>
            ),
        },
        {
            title: 'Status',
            dataIndex: 'errorMessage',
            key: 'errorMessage',
            render: (errorMessage) => (
                <Tag color={errorMessage ? 'error' : 'success'}>
                    {errorMessage ? 'Failed' : 'Success'}
                </Tag>
            ),
        },
        {
            title: 'Prediction',
            dataIndex: 'prediction',
            key: 'prediction',
            render: (text, record) => {
                if (!text) return '-';

                const color = getPredictionColor(text, allLabels);

                // Display both the prediction and the display name if available
                const hasDisplayName = record.display_name && record.display_name !== text;

                return (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        {text}

                        {hasDisplayName && (
                            <div style={{
                                background: `linear-gradient(135deg, ${color} 0%, ${color}dd 100%)`,
                                color: 'white',
                                fontSize: '13px',
                                padding: '2px 10px',
                                borderRadius: '12px',
                                boxShadow: `0 2px 6px ${color}50`,
                                fontWeight: 'bold',
                                lineHeight: '1.2'
                            }}>
                                {record.display_name}
                            </div>
                        )}
                    </div>
                );
            }
        },
    ];

    // Update local pagination when totalRecords changes
    useEffect(() => {
        if (totalRecords > 0) {
            setPagination(prev => ({
                ...prev,
                total: totalRecords
            }));
        }
    }, [totalRecords]);

    return (
        <div>
            {loading ? (
                <div style={{ minHeight: '300px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <Spin />
                </div>
            ) : (
                <Table
                    dataSource={displayData?.map(record => ({
                        ...record,
                        key: record.id
                    }))}
                    columns={columns}
                    rowKey="id"
                    pagination={{
                        current: pagination.current,
                        pageSize: pagination.pageSize,
                        total: totalRecords,
                        showSizeChanger: true,
                        pageSizeOptions: ['10', '20', '50', '100'],
                        showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`
                    }}
                    onChange={handleTableChange}
                    scroll={{ x: 'max-content' }}
                    expandable={{
                        expandedRowRender: record => (
                            <div style={{ margin: 0 }}>
                                <div style={{ marginBottom: "10px" }}><strong>Example:</strong></div>
                                <div>{record.example}</div>
                                {record.errorMessage && <div>
                                    <div style={{ marginBottom: "10px", marginTop: "20px", color: colors.red }}><strong>Error:</strong></div>
                                    <div style={{ color: colors.red }}>{record.errorMessage}</div>
                                </div>
                                }
                            </div>
                        ),
                        rowExpandable: record => Boolean(record.example),
                        expandRowByClick: false
                    }}
                />
            )}
        </div>
    );
});

export default RawDataTable; 